import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { gameSessionsAPI } from '../lib/supabase';
import { Trophy, Medal, Award, ArrowLeft, Filter } from 'lucide-react';

const Leaderboard = () => {
  const navigate = useNavigate();
  const [leaderboard, setLeaderboard] = useState([]);
  const [filter, setFilter] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchLeaderboard = useCallback(async () => {
    try {
      const data = await gameSessionsAPI.getLeaderboard(filter, 10);
      setLeaderboard(data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchLeaderboard();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchLeaderboard]);

  const fetchStats = async () => {
    try {
      const data = await gameSessionsAPI.getStats();
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy className="w-6 h-6 text-accent" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-primary" />;
    if (rank === 3) return <Award className="w-6 h-6 text-secondary" />;
    return <span className="text-muted-foreground font-bold">#{rank}</span>;
  };

  const getGameTypeLabel = (type) => {
    const labels = {
      combat: 'Combat Cardio',
      jump: 'Jump Master',
      mirror: 'Mirror Wipe'
    };
    return labels[type] || type;
  };

  return (
    <div className="min-h-screen w-full bg-black relative overflow-hidden">
      <div 
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1769120062258-710d93c75444?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGRpZ2l0YWwlMjB0dW5uZWwlMjBuZW9ufGVufDB8fHx8MTc3MDU0MDU5N3ww&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      />
      
      <div className="relative z-10 p-6 max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <motion.button
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/')}
            className="flex items-center gap-2 bg-black/60 backdrop-blur-xl border border-primary/30 rounded-lg px-4 py-2"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5 text-primary" />
            <span className="font-heading font-bold uppercase text-primary">Back</span>
          </motion.button>

          <motion.h1
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="font-heading text-4xl md:text-6xl font-black uppercase tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent"
          >
            LEADERBOARD
          </motion.h1>

          <div className="w-24" />
        </div>

        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="flex items-center gap-3 mb-6 justify-center"
        >
          <Filter className="w-5 h-5 text-muted-foreground" />
          {['all', 'combat', 'jump', 'mirror'].map((type) => (
            <button
              key={type}
              onClick={() => setFilter(type === 'all' ? null : type)}
              className={`px-4 py-2 rounded-lg font-heading font-semibold uppercase text-sm transition-all ${
                (type === 'all' && !filter) || filter === type
                  ? 'bg-primary text-black shadow-neon-blue'
                  : 'bg-black/40 text-muted-foreground border border-border hover:border-primary/50'
              }`}
              data-testid={`filter-${type}`}
            >
              {type === 'all' ? 'All Games' : getGameTypeLabel(type)}
            </button>
          ))}
        </motion.div>

        {stats && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8"
          >
            <div className="bg-black/60 backdrop-blur-xl border border-primary/30 rounded-xl p-4">
              <div className="text-muted-foreground text-sm uppercase tracking-wider mb-1">Total Sessions</div>
              <div className="text-3xl font-heading font-black text-primary">{stats.total_sessions}</div>
            </div>
          </motion.div>
        )}

        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="bg-black/60 backdrop-blur-xl border border-border rounded-xl overflow-hidden"
        >
          {loading ? (
            <div className="p-12 text-center">
              <div className="text-primary font-heading text-lg animate-pulse-neon">LOADING...</div>
            </div>
          ) : leaderboard.length === 0 ? (
            <div className="p-12 text-center">
              <Trophy className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <div className="text-muted-foreground font-body">No scores yet. Be the first to play!</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/20">
                  <tr>
                    <th className="px-6 py-4 text-left font-heading text-sm uppercase tracking-wider text-muted-foreground">Rank</th>
                    <th className="px-6 py-4 text-left font-heading text-sm uppercase tracking-wider text-muted-foreground">Player</th>
                    <th className="px-6 py-4 text-left font-heading text-sm uppercase tracking-wider text-muted-foreground">Game</th>
                    <th className="px-6 py-4 text-right font-heading text-sm uppercase tracking-wider text-muted-foreground">Score</th>
                    <th className="px-6 py-4 text-right font-heading text-sm uppercase tracking-wider text-muted-foreground">Accuracy</th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboard.map((entry, index) => (
                    <motion.tr
                      key={index}
                      initial={{ x: -20, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ delay: index * 0.05 }}
                      className="border-t border-border hover:bg-primary/5 transition-colors"
                      data-testid="leaderboard-entry"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          {getRankIcon(entry.rank)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-heading font-semibold text-foreground">{entry.player_name}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-muted-foreground font-body text-sm uppercase">
                          {getGameTypeLabel(entry.game_type)}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="font-heading font-bold text-xl text-accent">{entry.score}</div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="font-heading font-semibold text-primary">{Number(entry.accuracy).toFixed(0)}%</div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default Leaderboard;
